import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import format from 'date-fns/format';
import cn from 'classnames';

import PaymentStatusSummary from 'components/common/InfoPopup/PaymentStatusSummary';
import { FORMAT_DATE } from 'constants/time';
import CopyIcon from '../../../img/copy.svg?react';

import styles from './styles.module.scss';

interface AboutCompanyProps {
  companyData: any;
  companyId: string | undefined;
}

export default function AboutCompany({ companyData, companyId }: AboutCompanyProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const phone = companyData.phone;
  const formattedPhone = phone ? (phone.startsWith('+') ? phone : `+${phone}`) : '';

  const handleCopyPhone = useCallback(() => {
    if (!formattedPhone) return;
    navigator.clipboard.writeText(formattedPhone).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [formattedPhone]);

  const siteUrl = companyData.site;
  const formattedSiteUrl = siteUrl
    ? siteUrl.startsWith('http') ? siteUrl : `https://${siteUrl}`
    : '';

  return (
    <div className={styles.aboutSection}>
      {/* Основные данные */}
      <div className={styles.sectionBlock}>
        <h3 className={styles.sectionTitle}>{t('companyPage.about.mainData')}</h3>
        <div className={styles.fieldsRow}>
          <div className={styles.fieldItem}>
            <span className={styles.fieldLabel}>{t('companyPage.about.legalAddress')}</span>
            <span className={styles.fieldValue}>{companyData.legal_address || '-'}</span>
          </div>
          <div className={styles.fieldItem}>
            <span className={styles.fieldLabel}>{t('companyPage.about.postAddress')}</span>
            <span className={styles.fieldValue}>{companyData.post_address || '-'}</span>
          </div>
          <div className={styles.fieldItem}>
            <span className={styles.fieldLabel}>{t('companyPage.about.legalForm')}</span>
            <span className={styles.fieldValue}>{companyData.legal_form || '-'}</span>
          </div>
        </div>
      </div>

      <hr className={styles.divider} />

      {/* Контактные данные */}
      <div className={styles.sectionBlock}>
        <h3 className={styles.sectionTitle}>{t('companyPage.about.contactData')}:</h3>
        <div className={styles.fieldsRow}>
          <div className={styles.fieldItem}>
            <span className={styles.fieldLabel}>{t('companyPage.about.phone')}</span>
            <div className={styles.phoneRow}>
              {formattedPhone ? (
                <>
                  <a href={`tel:${formattedPhone}`} className={styles.linkValue}>
                    {formattedPhone}
                  </a>
                  <button
                    type="button"
                    className={cn(
                      styles.copyButton,
                      copied ? styles.copyButtonActive : styles.copyButtonInactive,
                    )}
                    onClick={handleCopyPhone}
                    title={copied ? t('common.copied') : t('common.copy')}
                  >
                    <CopyIcon className={styles.copyIcon} />
                  </button>
                </>
              ) : (
                <span className={styles.fieldValue}>-</span>
              )}
            </div>
          </div>
          <div className={styles.fieldItem}>
            <span className={styles.fieldLabel}>Email</span>
            {companyData.email ? (
              <a href={`mailto:${companyData.email}`} className={styles.linkValue}>
                {companyData.email}
              </a>
            ) : (
              <span className={styles.fieldValue}>-</span>
            )}
          </div>
          <div className={styles.fieldItem}>
            <span className={styles.fieldLabel}>{t('companyPage.about.site')}</span>
            {siteUrl ? (
              <a
                href={`${formattedSiteUrl}?utm_source=blago.pro`}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.linkValue}
              >
                {formattedSiteUrl.replace(/^https?:\/\//, '')}
              </a>
            ) : (
              <span className={styles.fieldValue}>-</span>
            )}
          </div>
        </div>
      </div>

      <hr className={styles.divider} />

      {/* Регистрационные данные */}
      <div className={styles.sectionBlock}>
        <h3 className={styles.sectionTitle}>{t('companyPage.about.registrationData')}:</h3>
        <div className={styles.fieldsRow}>
          <div className={styles.fieldItem}>
            <span className={styles.fieldLabel}>{t('companyPage.about.foundingDate')}</span>
            <span className={styles.fieldValue}>
              {companyData.creation_date
                ? format(new Date(companyData.creation_date), FORMAT_DATE)
                : '-'}
            </span>
          </div>
          <div className={styles.fieldItem}>
            <span className={styles.fieldLabel}>{t('companyPage.about.platformDate')}</span>
            <span className={styles.fieldValue}>
              {companyData.created_at
                ? format(new Date(companyData.created_at), FORMAT_DATE)
                : '-'}
            </span>
          </div>
          <div className={styles.fieldItem}>
            <span className={styles.fieldLabel}>{t('companyPage.about.inn')}</span>
            <span className={styles.fieldValue}>{companyData.payer_number || '-'}</span>
          </div>
        </div>
      </div>

      <hr className={styles.divider} />

      {/* Достоверность оплаты */}
      <div className={styles.sectionBlock}>
        <h3 className={styles.sectionTitle}>{t('companyPage.about.paymentReliability')}</h3>
        <PaymentStatusSummary companyId={companyId} />
      </div>
    </div>
  );
}
